import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { UploadHttpError } from "@/lib/convex";
import { UnifiedWizard } from "@/components/unified/UnifiedWizard";
import { CLE_NOTE } from "@/components/unified/UnifiedWizard";
import { ouvrirDemandePublique } from "@/lib/ouvrirDemandePublique";
import { TestHumain } from "@/components/unified/TestHumain";
import { type FeedbackData } from "@/components/unified/FeedbackScreen";
import { FlowData } from "@/types/unified-flow";
import { Question, MultiEntryFieldDef } from "@/data/questions";
import { ArrowRight, Shield, Loader2, CheckCircle, Star, Send, AlertCircle, Save } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import posthog from "posthog-js";
import {
  api,
  type Id,
  logClientError,
  buildTranslatedAnswers,
  uploadDocument,
  fileSignature,
  toDbFormQuestionRows,
  type OcrContext,
} from "@/lib/convex";
import { setAiUsageContext } from "@/lib/gemini-client";
import { mapDbRowsToQuestions, DbFormQuestionRow } from "@/lib/questionMapper";
import { deriveConfigsFromConvex } from "@/lib/document-ocr-from-convex";
import { getUiTranslations, detectBrowserLanguage } from "@/lib/ui-translations";
import { SECTION_TRANSLATIONS } from "@/lib/section-translations";
import { LanguageSwitcher } from "@/components/unified/LanguageSwitcher";
import { getBrandingConfig } from "@/lib/branding";
import { useApplyBranding, useBranding } from "@/hooks/useBranding";
import { canonicalUrlFor, currentSubdomain } from "@/lib/subdomain";

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Écran de fin d'un formulaire public, tel que le serveur l'a attribué.
type PublicOutcome = {
  key: string;
  label: string;
  title: string;
  body: string;
  buttonLabel: string;
  buttonUrl: string;
};

// Seuls des liens web ou courriel sortent d'un bouton d'écran de fin : le
// texte vient d'un cabinet, jamais d'un script.
const safeOutcomeUrl = (url: string): string | null =>
  /^(https?:\/\/|mailto:)/i.test(url.trim()) ? url.trim() : null;

const FeedbackInline = ({ uiT: t, onSubmit }: { uiT: any; onSubmit: (fb: FeedbackData) => void }) => {
  const [rating, setRating] = useState<number | undefined>();
  const [hoveredStar, setHoveredStar] = useState<number | undefined>();
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [comment, setComment] = useState("");
  const [commentSent, setCommentSent] = useState(false);

  const save = (r: number, tags: Set<string>, c?: string) => {
    onSubmit({
      rating: r,
      tags: tags.size > 0 ? Array.from(tags) : undefined,
      comment: c?.trim() || undefined,
    });
  };

  const tags = [
    { id: "too_many_questions", label: t?.feedbackTagTooMany ?? "Trop de questions" },
    { id: "confusing", label: t?.feedbackTagConfusing ?? "Questions pas claires" },
    { id: "bugs", label: t?.feedbackTagBugs ?? "Bugs ou erreurs" },
    { id: "slow", label: t?.feedbackTagSlow ?? "Trop lent" },
    { id: "documents", label: t?.feedbackTagDocuments ?? "Problèmes avec les documents" },
  ];

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      if (rating) save(rating, next);
      return next;
    });
  };

  const showTags = rating !== undefined && rating <= 3;

  return (
    <div className="bg-card rounded-2xl shadow-card p-6 space-y-5">
      <h3 className="text-xl font-bold text-foreground text-center">
        {t?.feedbackTitle ?? "Votre avis nous aide"}
      </h3>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          {t?.feedbackRatingLabel ?? "Comment évalueriez-vous votre expérience globale ?"}
        </p>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((n) => {
            const filled = (hoveredStar ?? rating ?? 0) >= n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => {
                  setRating(n);
                  const newTags = n > 3 ? new Set<string>() : selectedTags;
                  if (n > 3) setSelectedTags(new Set());
                  save(n, newTags);
                }}
                onMouseEnter={() => setHoveredStar(n)}
                onMouseLeave={() => setHoveredStar(undefined)}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star className={cn("w-7 h-7 transition-colors", filled ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/40")} />
              </button>
            );
          })}
        </div>
      </div>

      {showTags && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            {t?.feedbackTagsLabel ?? "Qu'est-ce qui pourrait être amélioré ?"}
          </p>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag.id)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                  selectedTags.has(tag.id)
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/40"
                )}
              >
                {tag.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {rating !== undefined && !commentSent && (
        <div className="space-y-3">
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t?.feedbackCommentPlaceholder ?? "Écrivez vos commentaires ici..."}
            className="resize-none min-h-[60px]"
          />
          {comment.trim() && (
            <Button
              onClick={() => {
                save(rating, selectedTags, comment);
                setCommentSent(true);
              }}
              className="w-full gap-2"
            >
              <Send className="w-4 h-4" />
              {t?.feedbackContinue ?? "Envoyer mon avis"}
            </Button>
          )}
        </div>
      )}

      {commentSent && (
        <p className="text-sm text-muted-foreground text-center">
          {t?.feedbackThanks ?? "Merci pour votre avis !"}
        </p>
      )}
    </div>
  );
};

// Un lien collé dans une phrase arrive souvent avec sa ponctuation
// (« …favpv. », « …favpv) »). Un identifiant ne finit que par une lettre ou un
// chiffre : tout ce qui traîne après fait rejeter l'id par Convex et plante la page.
const nettoyerIdDuLien = (id?: string) => id?.replace(/[^a-z0-9]+$/i, "") || undefined;

/**
 * La forme sous laquelle une date s'enregistre : AAAA-MM-JJ, telle qu'elle a
 * été choisie, sans fuseau.
 *
 * Le calendrier rend MINUIT LOCAL. `toISOString()` convertit en UTC, et à
 * l'est de Greenwich minuit local tombe la veille au soir : un client à
 * Maurice qui choisit le 4 mai enregistrait « 2025-05-03T20:00:00.000Z », que
 * la relecture reprend par ses dix premiers caractères — le 3 mai. Il
 * corrigeait, et la date reculait encore.
 *
 * Mesuré le 2026-10-12 : juste à Toronto et en UTC, décalé d'un jour à
 * Maurice et à Paris. C'est déjà la convention des lignes de liste
 * (`toRowValue` dans repeat-groups) et celle que lisent le remplissage des PDF
 * et le catalogue.
 */
const dateEnregistrable = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const Index = () => {
  const params = useParams<{ clientId?: string; clientAppId?: string; supplementId?: string; publicFormId?: string }>();
  const clientId = nettoyerIdDuLien(params.clientId);
  const clientAppId = nettoyerIdDuLien(params.clientAppId);
  const supplementId = nettoyerIdDuLien(params.supplementId);
  const publicFormId = nettoyerIdDuLien(params.publicFormId);
  const isDirectAppLink = !!clientAppId;
  const isSupplementLink = !!supplementId;
  // /f/:publicFormId — formulaire public : pas de dossier derrière, la
  // soumission se crée quand la personne clique « Commencer ».
  const isPublicLink = !!publicFormId;
  const [started, setStarted] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);
  // Ce que le champ caché a récolté. Vide chez une personne, toujours.
  const [piege, setPiege] = useState("");
  // Null tant que le test d'humanité est éteint, ce qui est le cas aujourd'hui.
  const [jetonHumain, setJetonHumain] = useState<string | null>(null);
  const [initialData] = useState<FlowData>({});
  const [completed, setCompleted] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const feedbackCheckedRef = useRef(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const saveResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Convex Queries ──���───────────────────────────────────────────────────────
  const isLegacyUuid = clientId ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clientId) : false;

  const clientDataDirect = useQuery(
    api.clients.getClient,
    clientId && !isDirectAppLink && !isSupplementLink && !isLegacyUuid
      ? { clientId: clientId as Id<"clients"> }
      : "skip",
  );

  const clientDataLegacy = useQuery(
    api.clients.getClientByLegacyId,
    clientId && !isDirectAppLink && !isSupplementLink && isLegacyUuid
      ? { legacyId: clientId }
      : "skip",
  );

  const clientData = isLegacyUuid ? clientDataLegacy : clientDataDirect;

  const submissionData = useQuery(
    api.submissions.getSubmission,
    isDirectAppLink && clientAppId
      ? { submissionId: clientAppId as Id<"submissions"> }
      : "skip",
  );

  const supplementData = useQuery(
    api.supplementRequests.getSupplementRequest,
    isSupplementLink && supplementId
      ? { id: supplementId as Id<"supplementRequests"> }
      : "skip",
  );

  // ── Formulaire public ───────────────────────────────────────────────────────
  const publicFormData = useQuery(
    api.publicForms.getPublicForm,
    isPublicLink && publicFormId
      ? { publicFormId: publicFormId as Id<"publicForms"> }
      : "skip",
  );
  // La session est mémorisée dans ce navigateur seulement : partager le lien
  // public ne partage jamais les réponses de quelqu'un d'autre.
  const publicSessionKey = publicFormId ? `pf_session_${publicFormId}` : null;
  const [publicSessionId, setPublicSessionId] = useState<string | null>(() => {
    try {
      return publicSessionKey ? localStorage.getItem(publicSessionKey) : null;
    } catch {
      return null;
    }
  });
  const publicSubmissionData = useQuery(
    api.submissions.getSubmission,
    isPublicLink && publicSessionId
      ? { submissionId: publicSessionId as Id<"submissions"> }
      : "skip",
  );
  const publicOutcomeSaved = useQuery(
    api.publicForms.getPublicOutcome,
    isPublicLink && publicSessionId && publicSubmissionData?.status === "submitted"
      ? { submissionId: publicSessionId as Id<"submissions"> }
      : "skip",
  );
  const [publicOutcome, setPublicOutcome] = useState<PublicOutcome | null>(null);
  useEffect(() => {
    if (publicOutcomeSaved) setPublicOutcome(publicOutcomeSaved);
  }, [publicOutcomeSaved]);

  // ── Convex Mutations ────────────────────���───────────────────────────────────
  const initSubmissionMut = useMutation(api.submissions.initSubmission);
  const startPublicSubmissionMut = useMutation(api.publicForms.startPublicSubmission);
  const completePublicSubmissionMut = useMutation(api.publicForms.completePublicSubmission);
  const markStartedMut = useMutation(api.submissions.markStarted);
  const saveAnswerMut = useMutation(api.submissions.saveAnswer);
  const setCurrentQuestionMut = useMutation(api.submissions.setCurrentQuestion);
  const saveInitialAnswersMut = useMutation(api.submissions.saveInitialAnswers);
  const completeSubmissionMut = useMutation(api.submissions.completeSubmission);
  const saveFeedbackMut = useMutation(api.feedback.saveFeedback);
  const saveSupplementAnswerMut = useMutation(api.supplementRequests.saveSupplementAnswer);
  const completeSupplementMut = useMutation(api.supplementRequests.completeSupplementSubmission);
  const recordEmailConsentMut = useMutation(api.clients.recordEmailConsent);

  // Supplement-specific state
  const [supplementSections, setSupplementSections] = useState<string[]>([]);
  const [supplementSubmissionId, setSupplementSubmissionId] = useState<string | null>(null);

  // Derive common values from whichever route we're on
  const isLoading = isPublicLink
    ? publicFormData === undefined ||
      (!!publicSessionId && publicSubmissionData === undefined && !started)
    : isSupplementLink
      ? supplementData === undefined
      : isDirectAppLink
        ? submissionData === undefined
        : clientData === undefined;

  const supplementSubmission = supplementData?.submission;
  const firmId: string | undefined = isPublicLink
    ? publicFormData?.firmId
    : isSupplementLink
    ? supplementSubmission?.firmId
    : isDirectAppLink
      ? submissionData?.client?.firmId ?? submissionData?.firmId
      : clientData?.firmId;

  const formType: string | undefined = isPublicLink
    ? publicFormData?.name
    : isSupplementLink
    ? undefined
    : isDirectAppLink
      ? submissionData?.formDefinition?.name ?? submissionData?.formType ?? undefined
      : clientData?.formDefinitionName ?? undefined;

  const formDefinitionId: string | undefined = isPublicLink
    ? publicFormData?.formDefinitionId
    : isSupplementLink
    ? supplementSubmission?.formDefinitionId ?? supplementData?.client?.primaryFormDefinitionId ?? undefined
    : isDirectAppLink
      ? submissionData?.formDefinitionId ?? undefined
      : clientData?.primaryFormDefinitionId ?? undefined;

  // Phase 3: per-form language as source of truth for renderer locale.
  // Pulled from whichever query loaded the form (route-dependent). When unset,
  // falls back to browser detection (Phase 1+2 behavior — zero regression).
  const formLanguage: string | undefined = isPublicLink
    ? publicFormData?.language ?? undefined
    : isSupplementLink
    ? (supplementData as any)?.formDefinition?.language ?? undefined
    : isDirectAppLink
      ? submissionData?.formDefinition?.language ?? undefined
      : // Consultant-set client language (new intake) wins over the legacy
        // form's declared language, both over browser detection.
        (clientData as any)?.preferredLanguage ??
        (clientData as any)?.formDefinitionLanguage ??
        undefined;

  const resolvedClientId: string | undefined = isPublicLink
    ? undefined
    : isSupplementLink
    ? supplementData?.clientId
    : isDirectAppLink
      ? submissionData?.clientId ?? undefined
      : clientId;

  // Firm display name query
  const firmDisplayName = useQuery(
    api.firms.getFirmDisplayName,
    firmId ? { firmId: firmId as Id<"firms"> } : "skip",
  );

  // Firm branding (logo, colors, welcome copy). Null for a firm that never set
  // any, or whose add-on is off — the form then renders the stock Formio look.
  const storedBranding = useQuery(
    api.firms.getFormBranding,
    firmId ? { firmId: firmId as Id<"firms"> } : "skip",
  );

  // The firm's own address, if it claimed one. Answers even when the firm hasn't
  // picked colors yet, which is why it isn't read off the branding query.
  const firmSubdomain = useQuery(
    api.firms.getFormSubdomain,
    firmId ? { firmId: firmId as Id<"firms"> } : "skip",
  );

  // Opened on `cabinet.formio.ca`: the host names the firm, so its colors are
  // known before any client data has loaded. The client's own firm still wins
  // once it resolves — the path decides whose dossier this is, the host only
  // decides what it looks like, and a mismatch must not lock anyone out.
  const hostSubdomain = useMemo(() => currentSubdomain(), []);
  const subdomainFirm = useQuery(
    api.firms.getFirmBySubdomain,
    hostSubdomain ? { subdomain: hostSubdomain } : "skip",
  );

  // Form questions query
  const formQuestionsRaw = useQuery(
    api.questions.getFormQuestions,
    formDefinitionId
      ? { formDefinitionId: formDefinitionId as Id<"formDefinitions"> }
      : "skip",
  );

  // IMM-indexed intake (Slice 2 — `legalDocuments.immQuestions` per IMM →
  // union for the client's selected IMMs). When the client has any IMMs
  // attached, prefer this generator over the legacy formDefinition pull.
  // Enabled for direct-app links too (/app/{submissionId}): there
  // resolvedClientId = submissionData.clientId, a real clients.id — without
  // this, resuming via /app/ skipped the IMM intake and fell back to the
  // hardcoded default questionnaire. Excluded only for supplement + legacy-UUID
  // links (resolvedClientId isn't a current clients.id there). For legacy
  // form-based clients the generator returns no questions and we fall through to
  // the formDefinition path, so existing clients keep working.
  const immIntakeData = useQuery(
    api.legalDocuments.getIntakeForClient,
    resolvedClientId && !isSupplementLink && !isLegacyUuid
      ? { clientId: resolvedClientId as Id<"clients"> }
      : "skip",
  );

  // Per-client OCR config overrides: derive from the intake documents' catalogs
  // (which carry the firm's injected ocrFill wiring in their `fills`). Merged
  // over the global document configs inside UnifiedWizard so a firm-wired
  // question pre-fills when its source document is uploaded.
  const ocrConfigOverrides = useMemo(() => {
    if (!immIntakeData || !("documents" in immIntakeData)) return undefined;
    const rows = (immIntakeData.documents as any[])
      .map((d) => d.catalog)
      .filter((c) => c && c.key && Array.isArray(c.fills));
    if (rows.length === 0) return undefined;
    return deriveConfigsFromConvex(rows as any);
  }, [immIntakeData]);


  // Submission tracking
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [formStartedAt, setFormStartedAt] = useState<string | null>(null);
  const initCalledRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [publicStarting, setPublicStarting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAnswers, setSavedAnswers] = useState<FlowData | null>(null);
  const [resumeQuestionId, setResumeQuestionId] = useState<string | null>(null);
  // Réponses reprises d'un formulaire public, questionnaire jamais ouvert.
  const [prefilledNotStarted, setPrefilledNotStarted] = useState(false);

  // Persist the client's current position (by question externalId). Memoised so
  // the wizard's step-change effect fires only on an actual step change, not on
  // every render. Not wired for supplements (submissionId there isn't a
  // submissions row).
  const handlePositionChange = useCallback(
    (questionId: string) => {
      if (!submissionId) return;
      setCurrentQuestionMut({
        submissionId: submissionId as Id<"submissions">,
        questionId,
      }).catch(() => {});
    },
    [submissionId, setCurrentQuestionMut],
  );
  const [skippedSections, setSkippedSections] = useState<string[]>([]);
  const [documentOnly, setDocumentOnly] = useState(false);
  const [shouldResume, setShouldResume] = useState<boolean | null>(null);
  const [preferredLanguage, setPreferredLanguage] = useState<string>(() => {
    // Phase 3: form-level language overrides browser detection at mount time.
    // The Convex queries are async — formLanguage is typically undefined on
    // first render; the useEffect below re-syncs once it arrives.
    if (formLanguage) return formLanguage;
    return detectBrowserLanguage();
  });
  const t = getUiTranslations(preferredLanguage);

  // La liste « Ce dont vous aurez besoin » de l'accueil. Elle était fixe
  // (passeport, photo, tests de langue…) : vraie pour une personne qui
  // immigre, fausse pour un employeur qui remplit un formulaire d'EIMT. Elle
  // se lit maintenant sur les pièces que le dossier demande vraiment, les
  // obligatoires d'abord, et ne retombe sur l'ancienne liste que quand le
  // dossier n'en déclare aucune (anciens questionnaires sans IMM).
  const welcomeNeeds = useMemo<string[]>(() => {
    const docs =
      immIntakeData && "documents" in immIntakeData
        ? ((immIntakeData.documents as Array<{ label?: string; required?: boolean; dependsOn?: unknown }>) ?? [])
        : [];
    const labels = docs
      .filter((d) => d.required !== false && !d.dependsOn && typeof d.label === "string" && d.label.trim())
      .map((d) => d.label!.trim());
    const uniques = Array.from(new Set(labels)).slice(0, 6);
    if (uniques.length > 0) return uniques;
    return [t.needPassport, t.needId, t.needPhoto, t.needSpouseDocs, t.needLanguageTests];
  }, [immIntakeData, t]);


  useEffect(() => {
    document.documentElement.lang = preferredLanguage;
  }, [preferredLanguage]);

  const clientFirstName =
    clientData?.firstName || submissionData?.client?.firstName || t.defaultClientName;
  // No fallback: an empty family name must leave nothing behind, not a placeholder.
  const clientLastName = clientData?.lastName || submissionData?.client?.lastName || "";
  // A host may only paint the form of the firm it belongs to. Anyone can put any
  // firm's prefix in front of any client link, so a host that outranked the
  // dossier would let a firm stamp its logo and colors on a competitor's
  // questionnaire — the client would read "formulaire de X" under Y's brand.
  const hostBranding =
    subdomainFirm && (!firmId || subdomainFirm.firmId === firmId)
      ? (subdomainFirm.branding ?? null)
      : null;
  // The client's firm decides, including when it decides "no branding" (null).
  // The host only fills the gap while that answer is still in flight, which is
  // what keeps a firm's own address from flashing stock blue on first paint.
  const brandingSource = storedBranding !== undefined ? storedBranding : hostBranding;
  const brandingConfig = useMemo(() => getBrandingConfig(brandingSource), [brandingSource]);
  const branding = useBranding(
    brandingConfig,
    { prenom: clientFirstName, nom: clientLastName, cabinet: firmDisplayName || t.yourFirm },
    t,
  );
  // The mark in the top-left corner. A branded firm writes its own name there:
  // the client is filling a form for their lawyer, and the console preview has
  // always shown it that way. Unbranded stays "Formio", and the "Powered by"
  // line at the bottom keeps the attribution in both cases.
  const brandMark = brandingConfig && firmDisplayName ? firmDisplayName : "Formio";

  // Tokens go on <html>, so every screen wears the firm's colors: the welcome
  // page, the wizard, the completion screen, and the dropdowns that portal out
  // of the React tree.
  useApplyBranding(branding);

  // Any hostname serves this form, on purpose: a link sent before the firm had
  // an address, or with a typo in front of it, must still open. Once the firm's
  // address is known, move the client onto it — same path, right name in front,
  // so what they read in the address bar is the firm they're dealing with.
  useEffect(() => {
    if (typeof window === "undefined" || !firmSubdomain) return;
    const target = canonicalUrlFor(window.location.href, firmSubdomain);
    // `replace`, not `assign`: the wrong spelling has no business in the back
    // button, and this must never bounce between two hosts.
    if (target) window.location.replace(target);
  }, [firmSubdomain]);

  // Undefined = the query is still in flight. Painting now would show the stock
  // blue and repaint a beat later, so the loader holds until the firm's colors
  // are known — including for a client resuming mid-form, who now lands
  // straight in a branded wizard. On a firm address there is nothing to wait
  // for: the host already resolved the colors.
  const brandingLoading = !!firmId && storedBranding === undefined && !hostBranding;

  // Phase 3: when formLanguage arrives async, re-sync preferredLanguage to the
  // form's declared language UNLESS the submission already has a saved
  // preferredLanguage (resume case — respect the client's prior choice).
  useEffect(() => {
    if (!formLanguage) return;
    const savedLang =
      submissionData?.preferredLanguage ??
      supplementData?.submission?.preferredLanguage ??
      publicSubmissionData?.preferredLanguage;
    if (savedLang) return;
    setPreferredLanguage(formLanguage);
  }, [formLanguage, submissionData?.preferredLanguage, supplementData?.submission?.preferredLanguage, publicSubmissionData?.preferredLanguage]);

  const ocrCtx: OcrContext | undefined = firmId && submissionId
    ? { firmId, formType, submissionId }
    : undefined;

  // Rattache la consommation d'IA au bon cabinet. Le client Gemini n'a pas
  // accès au contexte React : on le lui dépose dès que le dossier est résolu.
  useEffect(() => {
    setAiUsageContext({ firmId: firmId ?? null, submissionId: submissionId ?? null });
  }, [firmId, submissionId]);

/**
 * Téléphone, tablette ou ordinateur, du point de vue du navigateur.
 *
 * On se fie à la largeur d'écran plutôt qu'à la chaîne d'identification du
 * navigateur : celle-ci ment de plus en plus (les iPad se déclarent en macOS),
 * alors que ce qui nous intéresse ici est justement la taille d'écran sur
 * laquelle la personne remplit son questionnaire.
 */
function detectDevice(): string {
  if (typeof window === "undefined") return "unknown";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

// PostHog
  const posthogIdentifiedRef = useRef(false);
  useEffect(() => {
    if (posthogIdentifiedRef.current) return;
    if (!resolvedClientId) return;
    posthogIdentifiedRef.current = true;
    posthog.identify(resolvedClientId, {
      firm_id: firmId,
      form_type: formType,
      submission_id: submissionId,
    });
    // Super properties so PostHog events (incl. the Survey "survey sent" event)
    // carry submission/firm context — lets you jump from a survey response to
    // that submission's session recording.
    posthog.register({
      firm_id: firmId,
      form_type: formType,
      submission_id: submissionId,
    });
  }, [resolvedClientId, firmId, formType, submissionId]);

  // DB-sourced questions
  const [dbQuestions, setDbQuestions] = useState<Record<string, Question> | null>(null);
  const [dbOrder, setDbOrder] = useState<string[] | null>(null);
  const [dbRows, setDbRows] = useState<DbFormQuestionRow[] | null>(null);

  // Convert Convex form questions to DB rows when query resolves. Two
  // sources: the legacy `getFormQuestions(formDefinitionId)` path and the
  // new IMM-indexed `getIntakeForClient` generator. When the new generator
  // returns any questions, it wins; otherwise we fall back to the legacy
  // formDefinition path so existing clients keep working unchanged.
  useEffect(() => {
    const immQuestions = immIntakeData && "questions" in immIntakeData
      ? immIntakeData.questions
      : undefined;
    const immDocuments = immIntakeData && "documents" in immIntakeData
      ? immIntakeData.documents
      : undefined;
    // Fire the IMM-intake branch whenever the generator returned ANYTHING —
    // questions OR documents. A demande type with only documents (no question)
    // must still render its uploads; otherwise we fall through to the legacy
    // form path, which shows the hardcoded default questionnaire (48 questions).
    if (
      (immQuestions && immQuestions.length > 0) ||
      (immDocuments && immDocuments.length > 0)
    ) {
      // Project the generator output into the shape `toDbFormQuestionRows`
      // expects from `getFormQuestions`. Each enriched question carries its
      // catalog row, plus per-IMM hints (order/section) used to rebuild
      // formQuestions-row equivalents.
      //
      // Use a stride of 10 (idx*10) for catalog questions so we can slot
      // document upload steps at fractional positions in between (e.g.
      // after sponsorshipMeta but before sponsor identity questions).
      const questionRows = (immQuestions ?? []).map((q: any, idx: number) => ({
        orderIndex: idx * 10,
        section: q.section ?? null,
        sectionTranslations: q.section ? SECTION_TRANSLATIONS[q.section] ?? null : null,
        // Effective conditional (formDependsOn ?? catalog.dependsOn), resolved by
        // getIntakeForClient. The question mapper reads dependsOn from the ROW
        // only (not the catalog), so passing null here silently dropped EVERY
        // IMM question's conditional logic.
        dependsOn: q.dependsOn ?? null,
        // Per-intake label override: lets one IMM reword a shared catalog
        // question (e.g. the PEQ asks "Citoyenneté" for the OCR-wired
        // `nationality` field, which IMM forms label "Nationalité"). Read from
        // an explicit `labelOverride` field — NOT `label`, which some
        // auto-extracted intakes carry as a debug artifact ("SECTION Qn: …").
        labelOverride: q.labelOverride ?? null,
        namedLabel: q.namedLabel ?? null,
        requiredOverride: q.required ?? null,
        question: q.catalog ?? undefined,
      }));

      // Doc upload steps: insert AFTER the setup ("meta") AND identity
      // questions, so the client has given their name BEFORE being asked for
      // documents. This matters two ways: (1) UX — a form must not open on
      // "your passport?" before asking who you are; (2) the passport OCR
      // verifies the extracted name against the client's entered name, which
      // isn't available if documents come first. Types with no "meta" category
      // (e.g. a work permit) previously anchored at a NEGATIVE index, pushing
      // documents ahead of every question — hence the passport showing first.
      const LEAD_CATEGORIES = new Set(["meta", "identity"]);
      const leadCount = (immQuestions ?? []).filter((q: any) =>
        LEAD_CATEGORIES.has(q.catalog?.category),
      ).length;
      // Sits just after the last lead question and just before the next
      // category. Floored so documents can never precede the first question.
      const docAnchorBase = Math.max(leadCount, 1) * 10 - 5;

      // Per-doc conditional display rules. Stored as `dependsOn` on the
      // synthetic question row, which the wizard's getVisibleQuestions
      // honors automatically.
      //
      // La table ci-dessous est le SECOURS : la condition vient d'abord du
      // type de demande (`requiredDocOverrides.dependsOn`, posée par le
      // cabinet), parce qu'une règle écrite en dur ici ne vaut que pour les
      // clés que Formio connaît d'avance — jamais pour un créneau qu'un
      // cabinet ajoute lui-même.
      const DOC_DEPENDS_ON: Record<string, { questionId: string; value: any } | null> = {
        // « oui » en minuscules : c'est le CODE enregistré par une question
        // oui/non, pas son libellé. La comparaison de `isShown` est stricte,
        // donc « Oui » ne s'ouvrait jamais et le passeport du cosignataire
        // n'était demandé à personne, même avec un cosignataire déclaré.
        cosignerPassport: { questionId: "hasCosigner", value: "oui" },
        // marriageCertificate and sponsoredPassport stay unconditional for
        // now — gating those needs maritalStatus / sponsoredRelationship
        // answers which come further down the flow.
      };

      // Fractional spacing so ALL documents fit in the (last-lead-question,
      // next-question) gap. Questions use a stride of 10; placing docs at
      // docAnchorBase + idx (steps of 1) overflowed the gap once there were
      // >5 docs, wedging the next real question (e.g. maritalStatus) INTO the
      // middle of the upload block. Dividing by (count+1) keeps every doc
      // strictly between the two surrounding questions, clustered together.
      const docCount = (immDocuments ?? []).length;
      const docRows = (immDocuments ?? [])
        .map((d: any, idx: number) => ({
          orderIndex: docAnchorBase + idx / (docCount + 1), // all sit in the identity→next gap
          section: "Pièces à téléverser",
          sectionTranslations: SECTION_TRANSLATIONS["Pièces à téléverser"] ?? null,
          dependsOn: d.dependsOn ?? DOC_DEPENDS_ON[d.key] ?? null,
          labelOverride: null,
          requiredOverride: d.required ?? false,
          question: {
            externalId: d.key,
            // Le nom affiché : celui que le type de demande ou le cabinet a posé
            // d'abord, le nom du catalogue ensuite. L'ordre inverse annulait tout
            // renommage : un cabinet qui rebaptisait une pièce ne voyait rien
            // changer côté client, et une pièce partagée gardait un nom écrit
            // pour un autre type de demande. `d.label` vaut déjà le nom du
            // catalogue quand personne n'a renommé, donc ce choix ne change rien
            // dans le cas courant. Un document personnalisé du cabinet, lui, n'a
            // pas de fiche et tombait déjà sur son propre libellé.
            label: d.label ?? d.catalog?.name ?? d.key,
            type: "document",
            // Per-language { en:{label,indication}, es:{…} } from the doc
            // catalog — questionMapper renders the doc name/description in the
            // client's language just like a normal question.
            translations: d.catalog?.translations ?? undefined,
            // Instruction shown under the doc name: firm override → canonical
            // catalog description → the terse expected-type hint, in that order.
            indication:
              d.description ??
              d.catalog?.description ??
              d.catalog?.expectedDocumentType ??
              undefined,
            // Les textes que le cabinet écrit pour CE document dans l'éditeur
            // de demande : un exemple, une aide, pourquoi ça compte, le mot de
            // la fin. Un document se vit comme une question, il mérite le même
            // accompagnement.
            example: d.example ?? undefined,
            whyImportantReason: d.whyImportantReason ?? undefined,
            whyImportantConsequence: d.whyImportantConsequence ?? undefined,
            successMessage: d.successMessage ?? undefined,
            isRequired: !!d.required,
            documentConfig: {
              acceptedFormats: ["image/jpeg", "image/png", "application/pdf"],
              maxSizeMB: 10,
              // Effective upload limit resolved server-side (per-demande-type >
              // firm-global > canonical default > 1). >1 flips the wizard into
              // multi-file mode; 1 keeps the original single-file flow.
              maxFiles: d.maxCount ?? d.catalog?.maxCount ?? 1,
            },
          },
        }));

      // Order the steps by their computed orderIndex — questionMapper preserves
      // array order and never sorts, so without this the concatenation order
      // wins and documents (listed first) always show before the questions,
      // regardless of their anchor. Sorting makes the anchoring actually apply:
      // identity questions first, then documents, then the rest.
      const orderedRows = [...docRows, ...questionRows].sort(
        (a, b) => a.orderIndex - b.orderIndex,
      );
      setDbRows(toDbFormQuestionRows(orderedRows));
      return;
    }
    if (!formQuestionsRaw || formQuestionsRaw.length === 0) return;
    setDbRows(toDbFormQuestionRows(formQuestionsRaw));
  }, [formQuestionsRaw, immIntakeData]);

  // Map DB rows to questions whenever rows are fetched or language changes
  useEffect(() => {
    if (!dbRows) return;
    const { questions, order } = mapDbRowsToQuestions(dbRows, preferredLanguage);
    setDbQuestions(questions);
    setDbOrder(order);
  }, [preferredLanguage, dbRows]);

  // Helper: hydrate state from submission data
  const hydrateFromSubmission = (data: {
    _id?: string;
    submissionId?: string;
    answers?: any;
    skippedSections?: any;
    documentOnly?: boolean;
    metadata?: any;
    preferredLanguage?: string;
    currentQuestionId?: string;
  }) => {
    const id = data.submissionId ?? data._id;
    if (id) setSubmissionId(id);
    const startedAt = (data.metadata as { started_at?: string } | null)?.started_at ?? null;
    setFormStartedAt(startedAt);
    if (Array.isArray(data.skippedSections)) setSkippedSections(data.skippedSections);
    if (data.documentOnly === true) setDocumentOnly(true);
    if (data.preferredLanguage) setPreferredLanguage(data.preferredLanguage);
    if (data.currentQuestionId) setResumeQuestionId(data.currentQuestionId);
    if (data.answers && Object.keys(data.answers).length > 0) {
      setSavedAnswers(data.answers);
      // Des réponses reprises d'un formulaire public (dossier créé depuis une
      // réponse) ne sont pas un début : la personne voit d'abord l'accueil.
      const meta = (data.metadata as { started_at?: string; prefilled_from?: string } | null) ?? null;
      if (meta?.prefilled_from && !meta?.started_at) setPrefilledNotStarted(true);
      else setStarted(true);
    }
  };

  // FORMULAIRE PUBLIC : /f/:publicFormId — reprendre la session de ce
  // navigateur s'il y en a une, sinon afficher l'accueil.
  useEffect(() => {
    if (!isPublicLink || publicFormData === undefined || initCalledRef.current) return;
    if (publicFormData === null) return;
    if (publicSessionId && publicSubmissionData === undefined) return;
    initCalledRef.current = true;

    const sess = publicSessionId ? publicSubmissionData : null;
    if (!sess || sess.publicFormId !== publicFormData._id) {
      // Session absente ou périmée (réponse supprimée, autre formulaire).
      if (publicSessionId) {
        try { if (publicSessionKey) localStorage.removeItem(publicSessionKey); } catch { /* ignore */ }
        setPublicSessionId(null);
      }
      return;
    }
    if (sess.status === "submitted") {
      setSubmissionId(sess._id);
      setCompleted(true);
      return;
    }
    hydrateFromSubmission({
      _id: sess._id,
      answers: sess.answers,
      metadata: sess.metadata,
      preferredLanguage: sess.preferredLanguage,
      currentQuestionId: sess.currentQuestionId,
    });
    setStarted(true);
  }, [isPublicLink, publicFormData, publicSessionId, publicSessionKey, publicSubmissionData]);

  // NEW ROUTE: /app/:clientAppId — the submission already exists
  useEffect(() => {
    if (!isDirectAppLink || submissionData === undefined || initCalledRef.current) return;
    if (submissionData === null) return;
    initCalledRef.current = true;

    if (submissionData.status === 'submitted') {
      setSubmissionId(submissionData._id);
      setCompleted(true);
      const lsKey = `feedback_${submissionData._id}`;
      if (localStorage.getItem(lsKey)) {
        setFeedbackSent(true);
      }
      return;
    }
    hydrateFromSubmission({
      _id: submissionData._id,
      answers: submissionData.answers,
      skippedSections: submissionData.skippedSections,
      documentOnly: submissionData.documentOnly,
      metadata: submissionData.metadata,
      preferredLanguage: submissionData.preferredLanguage,
      currentQuestionId: submissionData.currentQuestionId,
    });
  }, [isDirectAppLink, submissionData]);

  // SUPPLEMENT ROUTE: /supplement/:supplementId
  useEffect(() => {
    if (!isSupplementLink || supplementData === undefined || initCalledRef.current) return;
    if (supplementData === null) return;
    initCalledRef.current = true;

    if (supplementData.status === 'submitted') {
      setSubmissionId(supplementData._id);
      setCompleted(true);
      return;
    }

    setSubmissionId(supplementData._id);
    setSupplementSections(supplementData.requestedSections ?? []);
    setSupplementSubmissionId(supplementData.submissionId);

    const ca = supplementData.submission;
    if (ca?.preferredLanguage) setPreferredLanguage(ca.preferredLanguage);

    const originalAnswers = (ca?.answers as Record<string, any>) || {};
    const supplementAnswers = (supplementData.answers as Record<string, any>) || {};
    const merged = { ...originalAnswers, ...supplementAnswers };

    if (Object.keys(merged).length > 0) {
      setSavedAnswers(merged);
    }
    setShouldResume(Object.keys(supplementAnswers).length > 0);
    setStarted(true);
  }, [isSupplementLink, supplementData]);

  // OLD ROUTE: /:clientId — create or resume a submission
  useEffect(() => {
    if (isDirectAppLink || isSupplementLink) return;
    if (!clientId || clientData === undefined || clientData === null || initCalledRef.current) return;
    initCalledRef.current = true;

    const clientName = `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim() || 'Client';

    initSubmissionMut({
      clientId: clientData._id,
      firmId: clientData.firmId,
      title: clientName,
      // Pas de sentinelle : un dossier mené par un type de demande n'a pas de
      // questionnaire hérité, donc pas de nom de définition — et « Inconnu »
      // n'est pas une absence, c'est une valeur. Figée ici, elle gagnait tous
      // les `||` en aval et titrait les cartes « Inconnu » au-dessus du nom du
      // client. Le champ est optionnel côté serveur : on le laisse vide.
      formType: clientData.formDefinitionName || undefined,
    }).then((result) => {
      if (result.alreadySubmitted) {
        setSubmissionId(result.submissionId);
        setCompleted(true);
        const lsKey = `feedback_${result.submissionId}`;
        if (localStorage.getItem(lsKey)) {
          setFeedbackSent(true);
        }
        return;
      }
      hydrateFromSubmission({
        submissionId: result.submissionId,
        answers: result.answers,
        skippedSections: result.skippedSections,
        documentOnly: result.documentOnly,
        metadata: result.metadata,
        preferredLanguage: result.preferredLanguage,
        currentQuestionId: result.currentQuestionId,
      });
    }).catch((err) => {
      console.error("Failed to init form submission:", err);
      logClientError('Index:initFormSubmission', err, { clientId });
    });
  }, [isDirectAppLink, isSupplementLink, clientId, clientData]);

  // Helper: transition saveStatus
  const markSaved = useCallback(() => {
    setSaveStatus('saved');
    if (saveResetTimer.current) clearTimeout(saveResetTimer.current);
    saveResetTimer.current = setTimeout(() => setSaveStatus('idle'), 3000);
  }, []);

  const markError = useCallback(() => {
    setSaveStatus('error');
    if (saveResetTimer.current) clearTimeout(saveResetTimer.current);
    saveResetTimer.current = setTimeout(() => setSaveStatus('idle'), 5000);
  }, []);

  // A failed document upload must not pass for a saved one: the wizard keeps
  // the file in memory, so the client can send it again from a toast that
  // stays until it succeeds. `retry` re-runs the same save with the same args.
  const uploadFailedToast = useCallback((error: unknown, retry: () => void) => {
    const tooLarge = error instanceof UploadHttpError && error.status === 413;
    toast.error(t.uploadFailedTitle, {
      id: "upload-failed",
      description: tooLarge ? t.uploadTooLargeBody : t.uploadFailedBody,
      duration: Infinity,
      action: { label: t.retry, onClick: () => { toast.dismiss("upload-failed"); retry(); } },
    });
  }, [t]);

  // La sauvegarde en vol de chaque question, pour les enchaîner dans l'ordre.
  const enVolParQuestion = useRef<Record<string, Promise<void>>>({});
  // Called by UnifiedWizard each time a question is answered
  const handleAnswerSaved = useCallback(async (
    questionId: string,
    value: any,
    extras?: Record<string, any>,
    questionType?: string,
    questionLabel?: string,
    multiEntryFields?: MultiEntryFieldDef[],
    // Per-file OCR verdict, keyed by fileSignature(file), so each uploaded
    // document is registered with its "ok"/"flagged" status for the consultant.
    verification?: Record<string, { status: string; feedback?: string }>,
    intermediaire = false,
  ) => {
    if (!submissionId || (!resolvedClientId && !isPublicLink)) return;
    // Les sauvegardes d'une même question partent à la file, dans l'ordre où
    // elles ont été demandées. Sans cela, une sauvegarde en cours de saisie
    // (« Ami ») et celle du Continuer (« Amina ») attendraient chacune leur
    // traduction, et la plus lente gagnerait : la base garderait « Ami ».
    const precedente = enVolParQuestion.current[questionId] ?? Promise.resolve();
    const courante = precedente.catch(() => undefined).then(async () => {
    setSaveStatus('saving');

    // Supplement mode: route saves to supplement_requests
    if (isSupplementLink) {
      try {
        const allAnswers = extras && Object.keys(extras).length > 0
          ? { [questionId]: value, ...extras }
          : { [questionId]: value };

        for (const [qId, val] of Object.entries(allAnswers)) {
          let storedValue = val ?? null;
          if (storedValue instanceof Date) {
            storedValue = dateEnregistrable(storedValue);
          }
          if (val instanceof File) {
            const { storageId, error } = await uploadDocument(supplementSubmissionId!, qId, val);
            if (error) { markError(); return; }
            storedValue = storageId;
          } else if (Array.isArray(val) && val.some((v) => v instanceof File)) {
            // A resumed multi-file answer mixes stored ids (strings) with the
            // files just added (File): upload only the latter, keep the rest.
            const ids: string[] = [];
            for (const item of val) {
              if (!(item instanceof File)) { if (typeof item === "string") ids.push(item); continue; }
              const { storageId, error } = await uploadDocument(supplementSubmissionId!, qId, item);
              if (error) { markError(); return; }
              if (storageId) ids.push(storageId);
            }
            storedValue = ids;
          }
          await saveSupplementAnswerMut({
            supplementId: submissionId as Id<"supplementRequests">,
            questionId: qId,
            value: storedValue,
          });
        }
        markSaved();
      } catch {
        markError();
      }
      return;
    }

    // Set started_at on the very first answer
    if (!formStartedAt) {
      markStartedMut({
        submissionId: submissionId as Id<"submissions">,
        device: detectDevice(),
      })
        .then(() => setFormStartedAt(new Date().toISOString()));
    }

    try {
      // Handle file uploads
      let storedValue = value ?? null;
      if (storedValue instanceof Date) {
        storedValue = dateEnregistrable(storedValue);
      }
      const retry = () =>
        void handleAnswerSaved(questionId, value, extras, questionType, questionLabel, multiEntryFields, verification, intermediaire);
      if (value instanceof File) {
        const { storageId, error } = await uploadDocument(submissionId, questionId, value, verification?.[fileSignature(value)]);
        if (error) {
          logClientError('Index:saveAnswer:upload', error, { submissionId, clientId: resolvedClientId, questionId });
          markError();
          uploadFailedToast(error, retry);
          return;
        }
        toast.dismiss("upload-failed");
        storedValue = storageId;
      } else if (Array.isArray(value) && value.some((v) => v instanceof File)) {
        // A resumed multi-file answer mixes stored ids (strings) with the files
        // just added (File). Only the Files are uploaded; the ids are kept. The
        // old `value[0] instanceof File` test skipped the whole upload when the
        // first entry was a stored id, and a raw File reached the mutation
        // ("File {} is not a supported Convex type", seen in prod on a
        // 6-file employment attestation).
        const ids: string[] = [];
        for (const item of value) {
          if (!(item instanceof File)) { if (typeof item === "string") ids.push(item); continue; }
          const { storageId, error } = await uploadDocument(submissionId, questionId, item, verification?.[fileSignature(item)]);
          if (error) {
            logClientError('Index:saveAnswer:upload', error, { submissionId, clientId: resolvedClientId, questionId });
            markError();
            uploadFailedToast(error, retry);
            return;
          }
          if (storageId) ids.push(storageId);
        }
        toast.dismiss("upload-failed");
        storedValue = ids;
      }

      if (extras && Object.keys(extras).length > 0) {
        // Bulk save: process file fields in extras too
        const processedExtras: Record<string, any> = {};
        for (const [key, val] of Object.entries(extras)) {
          if (val instanceof File) {
            const { storageId } = await uploadDocument(submissionId, key, val);
            processedExtras[key] = storageId;
          } else if (val instanceof Date) {
            processedExtras[key] = dateEnregistrable(val);
          } else {
            processedExtras[key] = val;
          }
        }

        const allAnswers = { [questionId]: storedValue, ...processedExtras };

        // Build translations client-side
        const preferredLang = preferredLanguage || 'fr';
        const translated = await buildTranslatedAnswers(
          allAnswers,
          null,
          preferredLang,
          (key) => dbQuestions?.[key]?.type ?? 'text',
          (key) => dbQuestions?.[key]?.label,
        );

        await saveInitialAnswersMut({
          submissionId: submissionId as Id<"submissions">,
          answers: allAnswers,
          translatedValues: translated ?? undefined,
        });
      } else {
        // Single answer save with translation. Une sauvegarde en cours de
        // saisie part brute : rien de partiel vers l'IA, et pas d'attente.
        const preferredLang = preferredLanguage || 'fr';
        const translated = intermediaire ? null : await buildTranslatedAnswers(
          { [questionId]: storedValue },
          null,
          preferredLang,
          () => questionType || 'text',
          () => questionLabel,
          () => multiEntryFields,
        );
        const translatedValue = translated?.[questionId];

        await saveAnswerMut({
          submissionId: submissionId as Id<"submissions">,
          questionId,
          value: storedValue,
          translatedValue,
          ...(intermediaire ? { sansTraduction: true } : {}),
        });
      }
      markSaved();
    } catch (err) {
      logClientError('Index:saveAnswer', err, { submissionId, clientId: resolvedClientId, questionId });
      markError();
    }
    });
    enVolParQuestion.current[questionId] = courante;
    return courante;
  }, [resolvedClientId, isPublicLink, submissionId, formStartedAt, markSaved, markError, uploadFailedToast, isSupplementLink, supplementSubmissionId, dbQuestions, preferredLanguage]);

  // Fire-and-forget: save feedback
  const handleFeedbackSave = useCallback((feedback: FeedbackData) => {
    if (!submissionId) return;
    const tagPrefix = feedback.tags?.length ? `[${feedback.tags.join(", ")}] ` : "";
    const fullComment = tagPrefix + (feedback.comment || "");
    saveFeedbackMut({
      submissionId: submissionId as Id<"submissions">,
      rating: feedback.rating,
      comment: fullComment.trim() || undefined,
    }).catch(() => {});
  }, [submissionId]);

  // Called when the form is fully completed
  const handleComplete = async (_data: FlowData) => {
    if (!submissionId) {
      setCompleted(true);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    // Le mot laissé au cabinet s'écrit sur le dernier écran : contrairement aux
    // réponses, il n'est jamais passé par la sauvegarde au fil de l'eau. Il part
    // donc ici, avant de clore, et son échec ne doit pas empêcher l'envoi : une
    // demande perdue serait pire qu'une note perdue.
    const note = _data[CLE_NOTE];
    if (typeof note === "string" && note.trim()) {
      try {
        await saveAnswerMut({
          submissionId: submissionId as Id<"submissions">,
          questionId: CLE_NOTE,
          value: note.trim(),
        });
      } catch (err) {
        logClientError("Index:noteAuCabinet", err, { submissionId });
      }
    }

    try {
      if (isSupplementLink) {
        // Build translations for supplement answers before completing
        const supplementAnswers = supplementData?.answers as Record<string, unknown> | undefined;
        const preferredLang = supplementData?.submission?.preferredLanguage ?? 'fr';
        let translatedValues: Record<string, unknown> | undefined;

        if (preferredLang !== 'fr' && supplementAnswers && Object.keys(supplementAnswers).length > 0) {
          const translated = await buildTranslatedAnswers(
            supplementAnswers,
            null,
            preferredLang,
            (key) => dbQuestions?.[key]?.type ?? 'text',
            (key) => dbQuestions?.[key]?.label,
            (key) => dbQuestions?.[key]?.multiEntryFields,
          );
          if (translated) translatedValues = translated;
        }

        await completeSupplementMut({
          supplementId: submissionId as Id<"supplementRequests">,
          translatedValues,
        });
        setSubmitting(false);
        setCompleted(true);
        return;
      }

      if (isPublicLink) {
        const res = await completePublicSubmissionMut({
          submissionId: submissionId as Id<"submissions">,
          piege: piege || undefined,
        });
        setPublicOutcome(res.outcome);
        setSubmitting(false);
        setCompleted(true);
        posthog.capture("public_form_completed", {
          submission_id: submissionId,
          firm_id: firmId,
          public_form_id: publicFormId,
          outcome: res.outcome?.key ?? null,
        });
        return;
      }

      await completeSubmissionMut({
        submissionId: submissionId as Id<"submissions">,
        startedAt: formStartedAt ?? undefined,
      });
      setSubmitting(false);
      setCompleted(true);

      // Trigger for the post-completion PostHog Survey (popover). Configure the
      // survey's display condition to fire on this event.
      posthog.capture("form_completed", {
        submission_id: submissionId,
        firm_id: firmId,
        form_type: formType,
      });
    } catch (err: any) {
      setSubmitting(false);
      logClientError('Index:completeFormSubmission', err, { clientId: resolvedClientId, submissionId });
      setSubmitError(err.message ?? "Unknown error");
    }
  };

  // Le remplissage des IMMs ne part plus d'ici. Il était lancé par un effet qui
  // attendait d'abord le retour d'une requête de complétion de groupe : entre
  // l'envoi et cet appel, il fallait que l'onglet du client reste ouvert. C'est
  // maintenant `completeSubmission` qui le planifie, côté serveur, au même
  // endroit que la notification au cabinet.

  // Loading state
  if (((clientId || clientAppId || supplementId || publicFormId) && isLoading) || brandingLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t.loading}</p>
        </div>
      </div>
    );
  }

  // Error state (null = not found)
  const hasError = (clientId && !isDirectAppLink && !isSupplementLink && clientData === null)
    || (isDirectAppLink && submissionData === null)
    || (isSupplementLink && supplementData === null)
    || (isPublicLink && publicFormData === null);

  if (hasError) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex justify-between items-center p-4">
          <span className="text-sm font-semibold text-primary leading-none">Formio</span>
          {!formLanguage && (
            <LanguageSwitcher value={preferredLanguage} onChange={setPreferredLanguage} />
          )}
        </div>
        <div className="flex items-center justify-center px-4 pt-8">
          <div className="max-w-md mx-auto w-full text-center">
            <div className="bg-card rounded-2xl shadow-card p-6">
              <div className="w-14 h-14 bg-destructive/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-7 h-7 text-destructive" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">{t.notFound}</h2>
              <p className="text-muted-foreground">
                {t.notFoundDesc}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire public dépublié : on ne prend plus de réponses (une réponse
  // déjà envoyée garde son écran de fin).
  if (isPublicLink && publicFormData && publicFormData.status !== "published" && !completed) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex justify-between items-center p-4">
          <span className="text-sm font-semibold text-primary leading-none" style={branding.primaryOnBackgroundStyle}>{brandMark}</span>
        </div>
        <div className="flex items-center justify-center px-4 pt-8">
          <div className="max-w-md mx-auto w-full text-center">
            <div className="bg-card rounded-2xl shadow-card p-6">
              <div className="w-14 h-14 bg-warning/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-7 h-7 text-warning" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">{t.publicFormClosed}</h2>
              <p className="text-muted-foreground">{t.publicFormClosedDesc}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No application type assigned (old route only)
  // IMM-indexed clients have `legalDocuments[]` instead of (or alongside)
  // `primaryFormDefinitionId`. A demande type with only firm-added custom
  // questions has an intake too (no IMM, but `demandeTypeId` is set and the
  // generator returns questions). Don't show "form unavailable" for any of these.
  const hasImmIntake =
    (clientData?.legalDocuments?.length ?? 0) > 0 ||
    !!(clientData as any)?.demandeTypeId ||
    !!(immIntakeData && "questions" in immIntakeData && immIntakeData.questions.length > 0);
  if (!isDirectAppLink && !isSupplementLink && clientId && clientData && !clientData.primaryFormDefinitionId && !hasImmIntake) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex justify-between items-center p-4">
          <span className="text-sm font-semibold text-primary leading-none">Formio</span>
          {!formLanguage && (
            <LanguageSwitcher value={preferredLanguage} onChange={setPreferredLanguage} />
          )}
        </div>
        <div className="flex items-center justify-center px-4 pt-8">
          <div className="max-w-md mx-auto w-full text-center">
            <div className="bg-card rounded-2xl shadow-card p-6">
              <div className="w-14 h-14 bg-warning/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-7 h-7 text-warning" aria-hidden="true" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">{t.formUnavailable}</h2>
              <p className="text-muted-foreground">
                {t.formUnavailableDesc}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Welcome screen
  if (!started && !completed && initCalledRef.current) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-xl mx-auto px-4 pt-4 pb-2 flex justify-between items-center">
          <span
            className="text-sm font-semibold text-primary leading-none"
            style={branding.primaryOnBackgroundStyle}
          >
            {brandMark}
          </span>
          {!formLanguage && (
            <LanguageSwitcher value={preferredLanguage} onChange={setPreferredLanguage} />
          )}
        </div>
        <div className="max-w-xl mx-auto px-4 pb-8 md:pb-12">
          <div className="bg-card rounded-2xl shadow-card p-6 md:p-8">
            <div className="text-center mb-6">
              {branding.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt={t.firmLogoAlt}
                  className="max-h-16 object-contain mx-auto mb-6"
                />
              ) : (
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
              )}
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                {isPublicLink
                  ? publicFormData?.intro?.title || publicFormData?.name
                  : branding.greeting}
              </h1>
              <p className="text-muted-foreground whitespace-pre-line">
                {isPublicLink
                  ? publicFormData?.intro?.message || t.publicWelcomeDefault
                  : branding.message}
              </p>
            </div>

            {/* Autosave reassurance — replaces the placebo Save & Quit button.
                Users only need to be told once, upfront, that their work is safe.
                Accent-tinted: this and the checklist are where the firm's second
                color shows up (accent defaults to the primary blue). */}
            <div className="mb-8 flex items-start gap-3 p-4 bg-accent/5 border border-accent/15 rounded-xl">
              <Save className="w-5 h-5 text-accent-on-card mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-foreground leading-relaxed">
                {t.autosaveReassurance}
              </p>
            </div>

            {branding.showChecklist && !isPublicLink && (
              <div className="mb-8">
                <h2 className="font-semibold text-foreground mb-4 text-base">
                  {t.whatYouNeed}
                </h2>
                <ul className="space-y-3 text-base text-foreground">
                  {welcomeNeeds.map((need) => (
                    <li key={need} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-accent-on-card mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{need}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mb-6 p-4 bg-muted/30 rounded-xl border border-border">
              <label className="flex items-start gap-3 cursor-pointer min-h-[44px]">
                <Checkbox
                  checked={emailConsent}
                  onCheckedChange={(checked) => setEmailConsent(checked === true)}
                  className="mt-0.5"
                />
                <span className="text-sm text-muted-foreground leading-relaxed">
                  {isPublicLink ? t.publicConsentPrefix : t.emailConsentPrefix}
                  <strong className="text-foreground font-bold">
                    {firmDisplayName || t.yourFirm}
                  </strong>
                  {isPublicLink ? t.publicConsentSuffix : t.emailConsentSuffix}
                </span>
              </label>
            </div>

            {isPublicLink && <TestHumain onJeton={setJetonHumain} language={preferredLanguage} />}

            <Button
              onClick={async () => {
                if (isPublicLink) {
                  if (!publicFormData || publicStarting) return;
                  setPublicStarting(true);
                  try {
                    // La route d'abord : elle seule voit d'où vient la demande,
                    // donc elle seule peut compter par appareil. Elle rend null
                    // quand cet environnement n'a pas son adresse de routes,
                    // et on repasse alors par l'ancien chemin.
                    // Le texte est recomposé à partir des trois morceaux qui
                    // viennent d'être affichés, dans la langue affichée. C'est
                    // ce qu'on doit pouvoir montrer si quelqu'un conteste : la
                    // date seule ne dit pas à quoi la personne a consenti, et
                    // ce texte changera.
                    const consentementTexte = [
                      t.publicConsentPrefix,
                      firmDisplayName || t.yourFirm,
                      t.publicConsentSuffix,
                    ].join("");
                    const parLaRoute = await ouvrirDemandePublique({
                      publicFormId: publicFormData._id,
                      preferredLanguage,
                      device: detectDevice(),
                      consentementTexte,
                      jetonHumain: jetonHumain ?? undefined,
                    });
                    if (parLaRoute && "trop" in parLaRoute) {
                      toast.error(t.publicTooMany);
                      setPublicStarting(false);
                      return;
                    }
                    if (parLaRoute && "testHumain" in parLaRoute) {
                      toast.error(t.publicHumanCheck);
                      setPublicStarting(false);
                      return;
                    }
                    const res = parLaRoute ?? await startPublicSubmissionMut({
                      publicFormId: publicFormData._id,
                      preferredLanguage,
                      device: detectDevice(),
                      consentementTexte,
                    });
                    setSubmissionId(res.submissionId);
                    setFormStartedAt(new Date().toISOString());
                    try { if (publicSessionKey) localStorage.setItem(publicSessionKey, res.submissionId); } catch { /* ignore */ }
                    setPublicSessionId(res.submissionId as Id<"submissions">);
                    setStarted(true);
                  } catch (err) {
                    logClientError('Index:startPublicSubmission', err, { publicFormId });
                    toast.error(t.submitErrorDesc);
                  } finally {
                    setPublicStarting(false);
                  }
                  return;
                }
                if (emailConsent && resolvedClientId) {
                  recordEmailConsentMut({ clientId: resolvedClientId as Id<"clients"> }).catch(() => {});
                }
                setStarted(true);
              }}
              disabled={!emailConsent || publicStarting}
              className="w-full h-14 text-lg font-medium"
            >
              {t.startForm}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground">
              {t.needHelp}{" "}
              <a
                href="mailto:support@example.com"
                className="text-primary hover:underline"
                style={branding.primaryOnBackgroundStyle}
              >
                {t.contactSupport}
              </a>
            </p>
          </div>
        </div>

        <div className="fixed bottom-4 left-4">
          <p className="text-xs text-muted-foreground/60">
            {t.poweredBy}{" "}
            <span className="font-medium text-primary" style={branding.primaryOnBackgroundStyle}>
              Formio
            </span>
          </p>
        </div>
      </div>
    );
  }

  // Submitting state
  if (submitting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t.submitting}</p>
        </div>
      </div>
    );
  }

  // Submission error
  if (submitError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="bg-card rounded-2xl shadow-card p-6">
            <h2 className="text-xl font-bold text-foreground mb-2">{t.submitError}</h2>
            <p className="text-muted-foreground mb-4">
              {t.submitErrorDesc}
            </p>
            <p className="text-xs text-muted-foreground/60 mb-4">{submitError}</p>
            <Button onClick={() => setSubmitError(null)}>{t.retry}</Button>
          </div>
        </div>
      </div>
    );
  }

  // Écran de fin d'un formulaire public : celui que les réponses ont désigné.
  if (completed && isPublicLink) {
    const o = publicOutcome;
    const href = o?.buttonUrl ? safeOutcomeUrl(o.buttonUrl) : null;
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="max-w-xl w-full mx-auto px-4">
          <div className="bg-card rounded-2xl shadow-card p-6 md:p-8 text-center mb-6">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {o?.title || t.publicSubmittedTitle}
            </h2>
            <p className="text-muted-foreground whitespace-pre-line">
              {o?.body || t.publicSubmittedDesc}
            </p>
            {href && (
              <Button asChild className="mt-6 h-12 px-6 text-base">
                <a href={href}>{o?.buttonLabel || href}</a>
              </Button>
            )}
          </div>
          {/* Une personne remplit souvent deux fois : pour elle, puis pour son
              conjoint ou un autre service. Sans ce bouton, la page restait sur
              l'écran de fin et il fallait savoir vider son navigateur. On
              oublie la demande envoyée et on repart de la première question ;
              celle qui vient d'être envoyée, elle, est déjà chez le cabinet. */}
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => {
                try {
                  if (publicSessionKey) localStorage.removeItem(publicSessionKey);
                } catch { /* ignore */ }
                window.location.reload();
              }}
            >
              {t.publicFillAgain}
            </Button>
          </div>
          <div className="text-center mt-4">
            <p className="text-xs text-muted-foreground/60">
              {t.poweredBy}{" "}
              <span className="font-medium text-primary" style={branding.primaryOnBackgroundStyle}>Formio</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (completed) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <div className="max-w-xl w-full mx-auto px-4">
          <div className="bg-card rounded-2xl shadow-card p-6 text-center mb-6">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-foreground mb-1">{t.formSubmitted}</h2>
            <p className="text-muted-foreground text-sm">
              {t.formSubmittedDesc}
            </p>
          </div>

          {!feedbackSent && (
            <FeedbackInline
              uiT={t}
              onSubmit={(feedback) => {
                handleFeedbackSave(feedback);
                if (submissionId) {
                  localStorage.setItem(`feedback_${submissionId}`, '1');
                }
              }}
            />
          )}

          <div className="text-center mt-4">
            <p className="text-xs text-muted-foreground/60">
              Powered by <span className="font-medium text-primary">Formio</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Wait for submission init AND db questions before rendering wizard.
  //
  // The wizard computes its resume position ONCE, when it mounts. A dossier
  // built from a demande type has no formDefinitionId, so this gate used to let
  // it mount before its intake questions arrived: the position was computed on
  // an empty list, the client landed back on question 1 every time, and the
  // wizard then saved that as its position, erasing the real one. Wait for the
  // intake query, and for its questions when it returned any.
  const intakeQueryEnabled = !!resolvedClientId && !isSupplementLink && !isLegacyUuid;
  const intakeHasContent =
    !!immIntakeData &&
    ((("questions" in immIntakeData) && (immIntakeData.questions?.length ?? 0) > 0) ||
      (("documents" in immIntakeData) && (immIntakeData.documents?.length ?? 0) > 0));
  const intakeLoading =
    intakeQueryEnabled && (immIntakeData === undefined || (intakeHasContent && !dbQuestions));
  if (!submissionId || (formDefinitionId && !dbQuestions) || intakeLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t.loading}</p>
        </div>
      </div>
    );
  }

  // Merge: saved answers from DB take priority
  const mergedInitialData = { ...initialData, ...(savedAnswers || {}) };

  // For supplement mode: invert requested sections into skippedSections
  const effectiveSkippedSections = (() => {
    if (!isSupplementLink || supplementSections.length === 0) return skippedSections;
    const allSections = new Set<string>();
    if (dbQuestions) {
      for (const q of Object.values(dbQuestions)) {
        if (q.section) allSections.add(q.section);
      }
    }
    return Array.from(allSections).filter(s => !supplementSections.includes(s));
  })();

  return (
    <>
    {/* Le piège. Un champ qu'aucune personne ne voit et qu'un programme qui
        parcourt la page remplit comme les autres, parce qu'il ne sait pas
        regarder. Il est poussé hors de l'écran plutôt que masqué : un robot un
        peu sérieux ignore ce qui est en `display: none`, mais suit rarement les
        coordonnées. Le nom est banal exprès, c'est celui qu'ils cherchent. */}
    {isPublicLink && (
      <input
        type="text"
        name="website"
        value={piege}
        onChange={(e) => setPiege(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
    )}
    <UnifiedWizard
      onComplete={handleComplete}
      onAnswerSaved={handleAnswerSaved}
      onFeedbackSave={isSupplementLink || isPublicLink ? undefined : handleFeedbackSave}
      initialData={mergedInitialData}
      applicationId={formDefinitionId}
      resumeFrom={shouldResume !== null ? shouldResume : (savedAnswers ? true : false)}
      resumeQuestionId={isSupplementLink ? undefined : (resumeQuestionId ?? undefined)}
      startAtFirstUnanswered={prefilledNotStarted}
      onPositionChange={isSupplementLink ? undefined : handlePositionChange}
      skippedSections={effectiveSkippedSections}
      brandMark={brandMark}
      documentOnly={documentOnly}
      dbQuestions={dbQuestions ?? undefined}
      dbOrder={dbOrder ?? undefined}
      ordreDuCabinet={
        !!immIntakeData && "ordreDuCabinet" in immIntakeData && !!immIntakeData.ordreDuCabinet
      }
      ocrCtx={isSupplementLink ? undefined : ocrCtx}
      ocrConfigOverrides={ocrConfigOverrides}
      saveStatus={saveStatus}
      uiT={t}
      preferredLanguage={preferredLanguage}
      onLanguageChange={formLanguage ? undefined : setPreferredLanguage}
    />
    </>
  );
};

export default Index;
